import * as React from "react";
// eslint-disable-next-line no-duplicate-imports
import { useEffect, useState, createRef } from "react";
import styled from "styled-components";
import {
  OriginThemeProvider,
  TrackingProvider,
} from "@origin-digital/ods-core";
import { OriginThemeProvider as ODSV1ThemeProvider } from "ods-core-v1";
import { LinkComponentProps } from "@origin-digital/ods-types";
import { trackingProviderCaptureClick } from "@origin-digital/reporting-client";
import {
  appDiagnostics,
  parseMarkdownEvent,
} from "@origin-digital/event-dispatcher";
import {
  ReviewAlertFooterService,
  TrustPilotModal,
} from "@origin-digital/platform-personalisation";

import {
  FontStyles,
  ResetStyles,
  IconFontStyles,
  BrazeStyles,
} from "../../styles/global";
import { KrakenAppContent, UnauthAppContent } from "../AppContent";
import { SuppressAndroidInstallPrompt } from "../SuppressAndroidInstallPrompt";
import { CrisisBanner } from "../Banner";

import { PrefetchContext, PrefetchedContent } from "../prefetch";
import { Footer } from "../Footer";
import Breadcrumbs, { getBreadcrumbsContent } from "../Breadcrumbs";
import { InAppMessagingService } from "../InAppMessagingService";

const MeshLink = React.forwardRef<any, LinkComponentProps>((props, ref) => {
  const { href, onClick } = parseMarkdownEvent(props.href);
  return (
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    <a
      ref={ref}
      {...props}
      href={href}
      onClick={(e) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, babel/no-unused-expressions
        onClick && onClick(e);
        return props.onClick && props.onClick(e);
      }}
    />
  );
});

const ContentWrapper = styled.div`
  flex: 1 0 auto;
  ${({ background }: { background: "gray" | "white" | undefined }) => {
    if (background === "gray") return `background-color: rgb(241, 241, 241);`;
    if (background === "white") return `background-color: white;`;
    return "";
  }};
`;

//TODO: Type this exactly to match what tal-core passes on.
export interface AppProps {
  title: string;
  actionBar?: any;
  authenticated?: boolean;
  background?: "gray" | "white";
  channel?: string;
  children?: React.ReactNode;
  prefetchContent?: Record<string, PrefetchedContent>;
  noSeed?: boolean;
  platform?: string;
  presets?: string[];
  showFooter?: boolean;
  showHeader?: boolean | "unauth" | "scoped";
  showNativeAppAd?: boolean;
  enableCrisisBanner?: boolean;
}

interface OetalApplicationRootProps {
  children?: React.ReactNode;
  showFooter?: boolean;
}

const ThemeProvider = ({
  children,
  noSeed,
}: Pick<AppProps, "children" | "noSeed">) => {
  return noSeed ? (
    //@ts-ignore fails when updating to ts 4
    <OriginThemeProvider linkComponent={MeshLink}>
      {children}
    </OriginThemeProvider>
  ) : (
    <OriginThemeProvider
      //@ts-ignore fails when updating to ts 4
      linkComponent={MeshLink}
      generatorClassNameOptions={{ seed: "tal" }}
    >
      {children}
    </OriginThemeProvider>
  );
};

// dev notes: OetalApplicationRoot sets a default height style to push the
// footer (if any) off the viewport and when an app is populated into it in the
// browser, the height style is removed.  This is done so the layout shift of
// the app forcing the footer down does not impact the core web-vital CLS
// figure.  As this default style is applied during SSR the resultant HTML will
// have that height in it.

// For applications that are longer than the viewport, the footer CLS shift is
// quite significant. Taking the footer off the viewport reduces it a lot.  For
// applications that are smaller than the viewport then the CLS reduction is not
// so much - but not many apps are smaller than the viewport.

// Note that this is only done when the footer is actually to be displayed and
// there are no children.  The only circumstance when there will be children is
// when a full-page SSR is underway - like with the home page.

// To detect an application being rendered as a child, we use a
// MutationObserver.  As soon as a child exists the height style is removed and
// the observer disconnected.
const OetalApplicationRoot = ({
  children,
  showFooter,
}: OetalApplicationRootProps) => {
  const [heightStyle, setHeightStyle] = useState(
    showFooter && !children ? { height: "2500px" } : {}
  );
  const ref = createRef<HTMLDivElement>();
  let observer;

  useEffect(() => {
    if (showFooter && !children) {
      const { current } = ref;
      const callback = (
        mutationsList: any,
        obs: { disconnect: () => void }
      ) => {
        for (const mutation of mutationsList) {
          if (mutation.type === "childList") {
            setHeightStyle({});
            obs.disconnect();
          }
        }
      };

      observer = new MutationObserver(callback);
      observer.observe(current, {
        attributes: false,
        childList: true,
        subtree: false,
      });
    }
  }, []);
  return (
    <div id="oetal-application-root" ref={ref} style={heightStyle}>
      {children ? children : null}
    </div>
  );
};

const App = ({
  actionBar,
  background,
  channel,
  children,
  prefetchContent = {},
  noSeed,
  platform,
  presets,
  showFooter = true,
  showHeader = true,
  showNativeAppAd = true,
  enableCrisisBanner = true,
  title,
}: AppProps) => {
  const isKraken =
    platform === "kraken" || (presets && presets.includes("kraken"));
  const isNativeChannel = channel === "native";
  const breadcrumbsContent = getBreadcrumbsContent(prefetchContent);

  const renderChildren = () => {
    appDiagnostics({ name: "talInitMicrositeRender" });
    const chld = children ? children : null;

    if (noSeed) {
      return chld;
    } else {
      return (
        <ODSV1ThemeProvider generatorClassNameOptions={{ seed: "app" }}>
          <OriginThemeProvider generatorClassNameOptions={{ seed: "app" }}>
            {chld}
          </OriginThemeProvider>
        </ODSV1ThemeProvider>
      );
    }
  };

  return (
    <>
      <FontStyles />
      <ResetStyles />
      <IconFontStyles />
      <BrazeStyles />

      <ThemeProvider noSeed={noSeed}>
        <TrackingProvider onTrackingCapture={trackingProviderCaptureClick}>
          <PrefetchContext.Provider value={prefetchContent}>
            <TrustPilotModal />
            <ReviewAlertFooterService />
            <InAppMessagingService />
            <ContentWrapper
              data-id="tal-content-wrapper"
              background={background}
            >
              {!showNativeAppAd && <SuppressAndroidInstallPrompt />}

              {enableCrisisBanner && !isNativeChannel && <CrisisBanner />}

              {isKraken ? (
                <KrakenAppContent
                  title={title}
                  showHeader={showHeader}
                  actionBar={actionBar}
                  enableCrisisBanner={enableCrisisBanner}
                />
              ) : (
                <UnauthAppContent
                  actionBar={actionBar}
                  showHeader={showHeader}
                  title={title}
                  enableCrisisBanner={enableCrisisBanner}
                />
              )}
              {breadcrumbsContent && <Breadcrumbs data={breadcrumbsContent} />}
              <OetalApplicationRoot showFooter={showFooter}>
                {renderChildren()}
              </OetalApplicationRoot>
            </ContentWrapper>
            {showFooter ? <Footer /> : null}
          </PrefetchContext.Provider>
        </TrackingProvider>
      </ThemeProvider>
    </>
  );
};

export { App };
